/**
 * Google Ads helpers for Vehicle Listing Ads (VLA) remarketing and lead
 * conversions. Everything here is a no-op unless NEXT_PUBLIC_GOOGLE_AW_ID is
 * set, so unconfigured deployments keep their current behaviour. Events are
 * pushed with the standard gtag() argument shape, which both a bare gtag.js
 * install and the Google tag inside GTM understand.
 */
import { LEAD_VALUES, type LeadType } from "@/utils/ga4-events";

export const AD_CLICK_PARAMS = ["gclid", "gbraid", "wbraid", "gad_source", "msclkid", "fbclid", "ttclid"] as const;

export function getGoogleAdsId(): string | undefined {
    const id = process.env["NEXT_PUBLIC_GOOGLE_AW_ID"]?.trim();
    return id ? id : undefined;
}

export function getGoogleAdsLeadLabel(): string | undefined {
    const label = process.env["NEXT_PUBLIC_GOOGLE_LEAD_CONVERT"]?.trim();
    return label ? label : undefined;
}

/** True when the landing URL carries a paid click id, so tags must not be deferred. */
export function hasAdClickId(search: string): boolean {
    if (!search) return false;
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return AD_CLICK_PARAMS.some((key) => Boolean(params.get(key)));
}

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

function gtag(...args: unknown[]): void {
    if (typeof window === "undefined") return;
    const w = window as GtagWindow;
    if (typeof w.gtag === "function") {
        w.gtag(...args);
        return;
    }
    w.dataLayer = w.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
}

export interface AdsVehicleView {
    /** Merchant Center feed id: the stock number. */
    id: string;
    price?: number;
}

/**
 * Dynamic remarketing signal for VLA. `id` must match the Merchant Center
 * vehicle feed id (the stock number) and `google_business_vertical` must be "vehicle".
 */
export function trackAdsVehicleView(vehicle: AdsVehicleView): void {
    const awId = getGoogleAdsId();
    if (!awId || !vehicle.id) return;
    const payload: Record<string, unknown> = {
        send_to: awId,
        items: [{ id: vehicle.id, google_business_vertical: "vehicle" }]
    };
    if (typeof vehicle.price === "number" && vehicle.price > 0) payload["value"] = vehicle.price;
    gtag("event", "view_item", payload);
}

export interface AdsLeadConversion {
    leadType: LeadType;
    /** Vehicle the lead is about, when there is one. */
    vin?: string;
    /** Stable id for the submission so Ads can dedupe retries. */
    transactionId?: string;
}

/** Google Ads conversion for a captured lead, using NEXT_PUBLIC_GOOGLE_LEAD_CONVERT as the label. */
export function trackAdsLeadConversion(lead: AdsLeadConversion): void {
    const awId = getGoogleAdsId();
    const label = getGoogleAdsLeadLabel();
    if (!awId || !label) return;
    const payload: Record<string, unknown> = {
        send_to: `${awId}/${label}`,
        value: LEAD_VALUES[lead.leadType],
        currency: "USD"
    };
    if (lead.transactionId) payload["transaction_id"] = lead.transactionId;
    if (lead.vin) payload["items"] = [{ id: lead.vin, google_business_vertical: "vehicle" }];
    gtag("event", "conversion", payload);
}
